// pages/superadmin/SuperAdminSystem.jsx
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { RefreshCw, Play, Hammer, CheckCircle2, XCircle, Server, Cpu, HardDrive, Clock, Zap, Package, Database, Download, Save } from 'lucide-react';
import ConfirmModal from '../../components/ConfirmModal';
import StatCard from '../../components/StatCard';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
const saApi = axios.create({ baseURL: API });
saApi.interceptors.request.use(cfg => {
  const t = localStorage.getItem('sa_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

function formatUptime(seconds) {
  if (!seconds) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}j ${h}h`;
  if (h > 0) return `${h}h ${m}min`;
  return `${m}min`;
}

const EMPTY_INSTALL = { installing:false, done:null, logs:'', showLogs:false };

// Bloc "Installer les dépendances" réutilisé identiquement pour la carte
// backend et la carte frontend — seul `target` ('backend'|'frontend') change.
function InstallDeps({ target, state, onInstall, onToggleLogs, disabled }) {
  const isDisabled = state.installing || disabled;
  return (
    <div style={{ marginTop:14, paddingTop:14, borderTop:'1px solid #2a2b35' }}>
      <p style={{ color:'#666', fontSize:11.5, lineHeight:1.5, marginBottom:10 }}>
        Supprime <code style={{ color:'#888', fontSize:10.5 }}>node_modules</code> puis réinstalle tout
        depuis zéro (plus lent qu'un simple npm install, mais répare aussi un dossier corrompu/incomplet).
      </p>
      {state.done !== null && (
        <div style={{ marginBottom:10, padding:'8px 12px', borderRadius:8, fontSize:12,
          background: state.done ? 'rgba(0,210,180,0.08)' : 'rgba(255,77,109,0.08)',
          border: `1px solid ${state.done ? '#00d2b4' : '#ff4d6d'}`,
          color: state.done ? '#00d2b4' : '#ff4d6d',
          display:'flex', alignItems:'center', gap:8 }}>
          {state.done ? <CheckCircle2 size={13}/> : <XCircle size={13}/>}
          {state.done ? 'Dépendances installées' : 'Échec npm install — voir les logs'}
          {state.logs && (
            <button onClick={onToggleLogs}
              style={{ marginLeft:'auto', background:'none', border:'none', color:'inherit',
                textDecoration:'underline', cursor:'pointer', fontSize:11, fontFamily:'inherit' }}>
              {state.showLogs ? 'Masquer' : 'Voir les logs'}
            </button>
          )}
        </div>
      )}
      {state.showLogs && state.logs && (
        <pre style={{ margin:'0 0 10px', padding:'10px 12px', background:'#0a0b0f',
          border:'1px solid #2a2b35', borderRadius:8, fontFamily:'monospace', fontSize:10.5,
          color:'#c8c8e0', lineHeight:1.5, maxHeight:160, overflowY:'auto',
          whiteSpace:'pre-wrap', wordBreak:'break-all' }}>
          {state.logs}
        </pre>
      )}
      <button onClick={onInstall} disabled={isDisabled}
        style={{ display:'flex', alignItems:'center', gap:7, padding:'8px 16px',
          borderRadius:9, border:'1px solid #2a2b35', cursor: isDisabled ? 'not-allowed' : 'pointer',
          background:'#1a1b24', color:'#aaa', fontFamily:'inherit', fontSize:13, fontWeight:600,
          opacity: isDisabled ? 0.7 : 1 }}>
        {state.installing
          ? <><RefreshCw size={12} style={{ animation:'spin 1s linear infinite' }}/> Installation…</>
          : <><Package size={12}/> Installer les dépendances (npm install)</>}
      </button>
    </div>
  );
}

export default function SuperAdminSystem() {
  const [status,       setStatus]      = useState(null);
  const [restarting,   setRestarting]  = useState(false);
  const [building,     setBuilding]    = useState(false);
  const [buildLogs,    setBuildLogs]   = useState('');
  const [buildDone,    setBuildDone]   = useState(null); // null | true | false
  const [restartMsg,   setRestartMsg]  = useState(null);
  const [confirmRestart, setConfirmRestart] = useState(false);
  const [confirmRestartAll, setConfirmRestartAll] = useState(false);
  const [restartingAll, setRestartingAll] = useState(false);
  const [installState, setInstallState] = useState({ backend: EMPTY_INSTALL, frontend: EMPTY_INSTALL });
  const logsRef    = useRef(null);
  const pollRef    = useRef(null);
  const logSizeRef = useRef(0);
  const installPollRefs    = useRef({ backend:null, frontend:null });
  const installLogSizeRefs = useRef({ backend:0, frontend:0 });

  // ── Sauvegardes DB ──────────────────────────────────────────
  const [backups,        setBackups]       = useState([]);
  const [retentionDays,  setRetentionDays] = useState(7);
  const [savingRetention, setSavingRetention] = useState(false);
  const [backupRunning,  setBackupRunning] = useState(false);
  const [backupLogs,     setBackupLogs]    = useState('');
  const [backupDone,     setBackupDone]    = useState(null);
  const backupPollRef    = useRef(null);
  const backupLogSizeRef = useRef(0);

  const loadBackups = useCallback(async () => {
    try {
      const r = await saApi.get('/superadmin/backups');
      setBackups(r.data.backups || []);
      setRetentionDays(r.data.config?.retentionDays ?? 7);
      setBackupRunning(!!r.data.inProgress);
    } catch (e) {}
  }, []);

  useEffect(() => { loadBackups(); }, [loadBackups]);

  const saveRetention = async () => {
    setSavingRetention(true);
    try {
      await saApi.put('/superadmin/backups/config', { retentionDays });
      loadBackups();
    } catch (e) {} finally { setSavingRetention(false); }
  };

  const runBackupNow = async () => {
    setBackupRunning(true);
    setBackupLogs('');
    setBackupDone(null);
    backupLogSizeRef.current = 0;
    let accumulated = '';
    try {
      await saApi.post('/superadmin/backups/run');
    } catch (e) {
      setBackupLogs('Erreur : ' + (e.response?.data?.error || e.message));
      setBackupRunning(false);
      return;
    }
    backupPollRef.current = setInterval(async () => {
      try {
        const r = await saApi.get(`/superadmin/backups/logs?since=${backupLogSizeRef.current}`);
        if (r.data.logs) {
          accumulated += r.data.logs;
          setBackupLogs(prev => prev + r.data.logs);
          backupLogSizeRef.current = r.data.size;
        }
        if (r.data.done) {
          clearInterval(backupPollRef.current);
          setBackupRunning(false);
          setBackupDone(accumulated.includes('✅'));
          loadBackups();
        }
      } catch (e) {}
    }, 1000);
  };

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)} Mo` : `${(bytes / 1024).toFixed(0)} Ko`;
  };

  const downloadBackup = (name) => {
    const token = localStorage.getItem('sa_token');
    window.open(`${API}/superadmin/backups/${encodeURIComponent(name)}/download?token=${encodeURIComponent(token || '')}`, '_blank');
  };

  const loadStatus = useCallback(async () => {
    try {
      const r = await saApi.get('/superadmin/system/status');
      setStatus(r.data);
    } catch(e) {
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 10000);
    return () => clearInterval(interval);
  }, [loadStatus]);

  // Auto-scroll logs
  useEffect(() => {
    if (logsRef.current) {
      logsRef.current.scrollTop = logsRef.current.scrollHeight;
    }
  }, [buildLogs]);

  // Logique de redémarrage factorisée : réutilisée par le bouton "Redémarrer
  // le backend" (après confirmation) et par "Tout redémarrer".
  const doRestart = async () => {
    setRestarting(true);
    setRestartMsg(null);
    try {
      await saApi.post('/superadmin/system/restart');
      setRestartMsg({ success: true, msg: 'Serveur redémarré avec succès' });
      setTimeout(loadStatus, 3000);
    } catch(e) {
      setRestartMsg({ success: false, msg: e.response?.data?.error || 'Échec du redémarrage' });
    } finally {
      setRestarting(false);
    }
  };

  const handleRestart = () => {
    setConfirmRestart(false);
    doRestart();
  };

  // La confirmation JS avant lancement a été retirée à la demande : le build
  // se lance directement au clic (les logs et le statut succès/échec restent
  // affichés ci-dessous, seule la popup de confirmation disparaît).
  //
  // Retourne une Promise qui se résout (avec true/false = succès) une fois le
  // build réellement terminé — pas juste lancé — pour que "Tout redémarrer"
  // puisse attendre la fin avant de passer au redémarrage backend (voir
  // handleRestartAll ci-dessous, qui explique pourquoi c'est indispensable).
  const handleBuild = () => {
    setBuilding(true);
    setBuildLogs('');
    setBuildDone(null);
    logSizeRef.current = 0;
    let accumulated = ''; // évite de dépendre du state React (async) pour détecter le ✅ final

    return new Promise((resolve) => {
      saApi.post('/superadmin/system/build')
        .then(() => {
          // Polling des logs toutes les secondes
          pollRef.current = setInterval(async () => {
            try {
              const r = await saApi.get(`/superadmin/system/build-logs?since=${logSizeRef.current}`);
              if (r.data.logs) {
                accumulated += r.data.logs;
                setBuildLogs(prev => prev + r.data.logs);
                logSizeRef.current = r.data.size;
              }
              if (r.data.done) {
                clearInterval(pollRef.current);
                const success = accumulated.includes('✅');
                setBuildDone(success);
                setBuilding(false);
                loadStatus();
                resolve(success);
              }
            } catch(e) {}
          }, 1000);
        })
        .catch(e => {
          setBuildLogs('Erreur lors du lancement du build : ' + (e.response?.data?.error || e.message));
          setBuildDone(false);
          setBuilding(false);
          resolve(false);
        });
    });
  };

  // "Tout redémarrer" : le build tourne comme SOUS-PROCESSUS du serveur
  // backend (voir routes/system.js, spawn('npm', ['run','build'])) — si on
  // redémarre le backend pendant qu'un build est en cours, PM2 tue le
  // process backend et, avec lui, ce sous-processus npm run build : le build
  // reste bloqué sur "En cours..." pour toujours (aucun marqueur ✅/❌ n'est
  // jamais écrit dans le fichier de logs, puisque le process qui devait
  // l'écrire n'existe plus). D'où l'enchaînement STRICT : build d'abord,
  // jusqu'à sa fin confirmée, puis redémarrage backend ensuite — jamais les
  // deux en parallèle.
  const handleRestartAll = async () => {
    setConfirmRestartAll(false);
    setRestartingAll(true);
    try {
      await handleBuild();
      await doRestart();
    } finally {
      setRestartingAll(false);
    }
  };

  const handleNpmInstall = async (target) => {
    setInstallState(prev => ({ ...prev, [target]: { ...EMPTY_INSTALL, installing:true } }));
    installLogSizeRefs.current[target] = 0;
    try {
      await saApi.post('/superadmin/system/npm-install', { target });
    } catch(e) {
      setInstallState(prev => ({ ...prev, [target]: {
        installing:false, done:false, showLogs:true,
        logs:'Erreur : ' + (e.response?.data?.error || e.message),
      } }));
      return;
    }
    installPollRefs.current[target] = setInterval(async () => {
      try {
        const r = await saApi.get(`/superadmin/system/npm-install-logs?target=${target}&since=${installLogSizeRefs.current[target]}`);
        if (r.data.logs) {
          setInstallState(prev => ({ ...prev, [target]: { ...prev[target], logs: prev[target].logs + r.data.logs } }));
          installLogSizeRefs.current[target] = r.data.size;
        }
        if (r.data.done) {
          clearInterval(installPollRefs.current[target]);
          setInstallState(prev => ({ ...prev, [target]: {
            ...prev[target], installing:false, done: prev[target].logs.includes('✅'),
          } }));
        }
      } catch(e) {}
    }, 1000);
  };

  const toggleInstallLogs = (target) => {
    setInstallState(prev => ({ ...prev, [target]: { ...prev[target], showLogs: !prev[target].showLogs } }));
  };

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (installPollRefs.current.backend)  clearInterval(installPollRefs.current.backend);
      if (installPollRefs.current.frontend) clearInterval(installPollRefs.current.frontend);
      if (backupPollRef.current) clearInterval(backupPollRef.current);
    };
  }, []);

  const pm2 = status?.pm2;
  // restartingAll couvre toute la séquence build→restart, y compris le
  // court instant entre les deux où `building`/`restarting` sont tous les
  // deux à false — sans ça les boutons individuels redeviendraient
  // brièvement cliquables entre les deux étapes.
  // Inclut désormais aussi les installations de dépendances (backend/frontend) :
  // le serveur refuse maintenant explicitement un build pendant une install
  // frontend en cours (et vice versa, voir routes/system.js) — le bouton
  // "Lancer le build" doit rester grisé pendant ce temps côté client aussi,
  // pas seulement recevoir une erreur 409 après un clic.
  const anyBusy = restarting || building || restartingAll
    || installState.backend.installing || installState.frontend.installing;

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24, flexWrap:'wrap', gap:10 }}>
        <div>
          <h2 style={{ margin:0, fontSize:20, fontWeight:800 }}>Système</h2>
          <p style={{ color:'#666', fontSize:13, marginTop:4 }}>Gestion du serveur et du frontend</p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={() => setConfirmRestartAll(true)} disabled={anyBusy}
            style={{ background: anyBusy ? '#2a2b35' : 'rgba(124,92,252,0.12)',
              border:'1px solid #7c5cfc', color: anyBusy ? '#888' : '#c4b5fd',
              borderRadius:8, padding:'8px 14px', cursor: anyBusy ? 'not-allowed' : 'pointer',
              display:'flex', alignItems:'center', gap:6, fontSize:13, fontWeight:700, fontFamily:'inherit' }}>
            {restartingAll
              ? <><RefreshCw size={13} style={{ animation:'spin 1s linear infinite' }}/> {building ? 'Build en cours…' : 'Redémarrage…'}</>
              : <><Zap size={13}/> Tout redémarrer (back + front)</>}
          </button>
          <button onClick={loadStatus}
            style={{ background:'#1e1f28', border:'1px solid #2a2b35', color:'#888',
              borderRadius:8, padding:'8px 14px', cursor:'pointer', display:'flex', alignItems:'center', gap:6, fontSize:13 }}>
            <RefreshCw size={13}/> Actualiser
          </button>
        </div>
      </div>

      {/* Statut serveur — tuiles compactes (une seule ligne chacune) */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:10, marginBottom:24 }}>
        <StatCard label="Statut PM2" icon={Server}
          value={pm2?.status === 'online' ? 'En ligne' : (pm2?.status || '—')}
          color={pm2?.status === 'online' ? '#00d2b4' : '#f5a623'}/>
        <StatCard label="Uptime" icon={Clock}
          value={pm2 ? formatUptime(pm2.uptime) : '—'} color="#7c5cfc"/>
        <StatCard label="Mémoire" icon={HardDrive}
          value={pm2?.memory ? `${pm2.memory} Mo` : (status ? `${status.memoryUsed} Mo` : '—')} color="#7c5cfc"/>
        <StatCard label="Node.js" icon={Cpu}
          value={status?.nodeVersion || '—'} color="#7c5cfc"/>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>

        {/* Redémarrage backend */}
        <div style={{ background:'#13141f', border:'1px solid #2a2b35', borderRadius:16, padding:24 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
            <RefreshCw size={16} color='#7c5cfc'/>
            <span style={{ fontWeight:700, fontSize:15 }}>Redémarrer le backend</span>
          </div>
          <p style={{ color:'#666', fontSize:13, lineHeight:1.6, marginBottom:20 }}>
            Relance le processus PM2 <code style={{ color:'#7c5cfc', fontSize:11 }}>{pm2?.name || 'backend'}</code>
            {' '}(détecté automatiquement — identique en preprod et en prod).
            Utile après le déploiement de fichiers backend (.js).
            Interruption de quelques secondes.
          </p>

          {restartMsg && (
            <div style={{ marginBottom:16, padding:'10px 14px', borderRadius:8, fontSize:13,
              background: restartMsg.success ? 'rgba(0,210,180,0.08)' : 'rgba(255,77,109,0.08)',
              border: `1px solid ${restartMsg.success ? '#00d2b4' : '#ff4d6d'}`,
              color: restartMsg.success ? '#00d2b4' : '#ff4d6d',
              display:'flex', alignItems:'center', gap:8 }}>
              {restartMsg.success ? <CheckCircle2 size={14}/> : <XCircle size={14}/>}
              {restartMsg.msg}
            </div>
          )}

          <button onClick={() => setConfirmRestart(true)} disabled={anyBusy}
            style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 20px',
              borderRadius:10, border:'none', cursor: anyBusy ? 'not-allowed' : 'pointer',
              background: anyBusy ? '#2a2b35' : '#7c5cfc',
              color:'#fff', fontFamily:'inherit', fontSize:14, fontWeight:700,
              opacity: anyBusy ? 0.7 : 1, transition:'all 0.15s' }}>
            {restarting
              ? <><RefreshCw size={14} style={{ animation:'spin 1s linear infinite' }}/> Redémarrage…</>
              : <><Play size={14}/> Redémarrer maintenant</>}
          </button>

          <InstallDeps target="backend" state={installState.backend}
            onInstall={() => handleNpmInstall('backend')}
            onToggleLogs={() => toggleInstallLogs('backend')}
            disabled={anyBusy}/>
        </div>

        {/* Build frontend */}
        <div style={{ background:'#13141f', border:'1px solid #2a2b35', borderRadius:16, padding:24 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
            <Hammer size={16} color='#f5a623'/>
            <span style={{ fontWeight:700, fontSize:15 }}>Rebuild frontend</span>
          </div>
          <p style={{ color:'#666', fontSize:13, lineHeight:1.6, marginBottom:20 }}>
            Lance <code style={{ color:'#f5a623', fontSize:11 }}>npm run build</code> dans le dossier frontend.
            Nécessaire après toute modification de fichier .jsx ou .css.
            Durée : 30 à 60 secondes.
          </p>

          {buildDone !== null && (
            <div style={{ marginBottom:16, padding:'10px 14px', borderRadius:8, fontSize:13,
              background: buildDone ? 'rgba(0,210,180,0.08)' : 'rgba(255,77,109,0.08)',
              border: `1px solid ${buildDone ? '#00d2b4' : '#ff4d6d'}`,
              color: buildDone ? '#00d2b4' : '#ff4d6d',
              display:'flex', alignItems:'center', gap:8 }}>
              {buildDone ? <CheckCircle2 size={14}/> : <XCircle size={14}/>}
              {buildDone ? 'Build terminé avec succès' : 'Build échoué — voir les logs'}
            </div>
          )}

          <button onClick={handleBuild} disabled={anyBusy}
            style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 20px',
              borderRadius:10, border:'none', cursor: anyBusy ? 'not-allowed' : 'pointer',
              background: anyBusy ? '#2a2b35' : '#f5a623',
              color: anyBusy ? '#888' : '#000', fontFamily:'inherit', fontSize:14, fontWeight:700,
              opacity: anyBusy ? 0.7 : 1, transition:'all 0.15s' }}>
            {building
              ? <><RefreshCw size={14} style={{ animation:'spin 1s linear infinite' }}/> Build en cours…</>
              : <><Hammer size={14}/> Lancer le build</>}
          </button>

          <InstallDeps target="frontend" state={installState.frontend}
            onInstall={() => handleNpmInstall('frontend')}
            onToggleLogs={() => toggleInstallLogs('frontend')}
            disabled={anyBusy}/>
        </div>
      </div>

      {/* Sauvegardes DB */}
      <div style={{ marginTop:20, background:'#13141f', border:'1px solid #2a2b35', borderRadius:16, padding:24 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12, marginBottom:8 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <Database size={16} color='#00d2b4'/>
            <span style={{ fontWeight:700, fontSize:15 }}>Sauvegardes de la base de données</span>
          </div>
          <button onClick={runBackupNow} disabled={backupRunning}
            style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 16px',
              borderRadius:10, border:'none', cursor: backupRunning ? 'not-allowed' : 'pointer',
              background: backupRunning ? '#2a2b35' : '#00d2b4',
              color: backupRunning ? '#888' : '#001', fontFamily:'inherit', fontSize:13, fontWeight:700,
              opacity: backupRunning ? 0.7 : 1 }}>
            {backupRunning
              ? <><RefreshCw size={13} style={{ animation:'spin 1s linear infinite' }}/> Sauvegarde en cours…</>
              : <><Save size={13}/> Sauvegarder maintenant</>}
          </button>
        </div>
        <p style={{ color:'#666', fontSize:13, lineHeight:1.6, marginBottom:16 }}>
          Sauvegarde automatique chaque nuit à 3h (pg_dump, format compressé), stockée localement sur le
          serveur. Les sauvegardes plus anciennes que la rétention configurée sont supprimées automatiquement.
        </p>

        {/* Rétention */}
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:18, flexWrap:'wrap' }}>
          <label style={{ fontSize:13, color:'#aaa' }}>Rétention (jours) :</label>
          <input type="number" min={1} max={365} value={retentionDays}
            onChange={e => setRetentionDays(e.target.value)}
            style={{ width:70, background:'#0a0b0f', border:'1px solid #2a2b35', borderRadius:8,
              color:'#fff', fontFamily:'inherit', fontSize:13, padding:'6px 10px' }}/>
          <button onClick={saveRetention} disabled={savingRetention}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 12px',
              borderRadius:8, border:'1px solid #2a2b35', cursor: savingRetention ? 'not-allowed' : 'pointer',
              background:'#1a1b24', color:'#aaa', fontFamily:'inherit', fontSize:12, fontWeight:600 }}>
            {savingRetention ? '…' : 'Enregistrer'}
          </button>
        </div>

        {backupDone !== null && (
          <div style={{ marginBottom:14, padding:'10px 14px', borderRadius:8, fontSize:13,
            background: backupDone ? 'rgba(0,210,180,0.08)' : 'rgba(255,77,109,0.08)',
            border: `1px solid ${backupDone ? '#00d2b4' : '#ff4d6d'}`,
            color: backupDone ? '#00d2b4' : '#ff4d6d',
            display:'flex', alignItems:'center', gap:8 }}>
            {backupDone ? <CheckCircle2 size={14}/> : <XCircle size={14}/>}
            {backupDone ? 'Sauvegarde terminée avec succès' : 'Échec de la sauvegarde — voir les logs'}
          </div>
        )}

        {(backupLogs || backupRunning) && (
          <pre style={{ margin:'0 0 16px', padding:'12px 14px', background:'#0a0b0f',
            border:'1px solid #2a2b35', borderRadius:8, fontFamily:'monospace', fontSize:10.5,
            color:'#c8c8e0', lineHeight:1.5, maxHeight:160, overflowY:'auto',
            whiteSpace:'pre-wrap', wordBreak:'break-all' }}>
            {backupLogs || ' '}
          </pre>
        )}

        {/* Liste des sauvegardes */}
        {backups.length === 0 ? (
          <p style={{ color:'#666', fontSize:13 }}>Aucune sauvegarde pour le moment.</p>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {backups.map(b => (
              <div key={b.name} style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                padding:'8px 12px', background:'#1a1b24', border:'1px solid #2a2b35', borderRadius:8, fontSize:12.5 }}>
                <div style={{ display:'flex', flexDirection:'column' }}>
                  <span style={{ color:'#ddd', fontFamily:'monospace' }}>{b.name}</span>
                  <span style={{ color:'#666', fontSize:11 }}>
                    {new Date(b.createdAt).toLocaleString('fr-FR')} · {formatSize(b.size)}
                  </span>
                </div>
                <button onClick={() => downloadBackup(b.name)}
                  style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 12px',
                    borderRadius:8, border:'1px solid #2a2b35', cursor:'pointer',
                    background:'#0a0b0f', color:'#aaa', fontFamily:'inherit', fontSize:11.5, fontWeight:600 }}>
                  <Download size={12}/> Télécharger
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Logs build */}
      {(buildLogs || building) && (
        <div style={{ marginTop:20, background:'#0a0b0f', border:'1px solid #2a2b35',
          borderRadius:12, overflow:'hidden' }}>
          <div style={{ padding:'10px 16px', borderBottom:'1px solid #2a2b35',
            display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#888' }}>
            <Hammer size={12}/>
            Logs du build
            {building && <span style={{ marginLeft:8, color:'#f5a623', fontSize:11 }}>● En cours…</span>}
            {buildDone === true  && <span style={{ marginLeft:8, color:'#00d2b4', fontSize:11 }}>✓ Terminé</span>}
            {buildDone === false && <span style={{ marginLeft:8, color:'#ff4d6d', fontSize:11 }}>✗ Échec</span>}
          </div>
          <pre ref={logsRef}
            style={{ margin:0, padding:'16px', fontFamily:'monospace', fontSize:11,
              color:'#c8c8e0', lineHeight:1.6, maxHeight:320, overflowY:'auto',
              whiteSpace:'pre-wrap', wordBreak:'break-all' }}>
            {buildLogs || ' '}
          </pre>
        </div>
      )}

      {confirmRestart && (
        <ConfirmModal
          title="Redémarrer le backend"
          message="Redémarrer le serveur backend ? Les connexions actives seront interrompues quelques secondes."
          confirmLabel="Redémarrer"
          danger
          loading={restarting}
          onConfirm={handleRestart}
          onCancel={() => setConfirmRestart(false)}
        />
      )}

      {confirmRestartAll && (
        <ConfirmModal
          title="Tout redémarrer"
          message="Ceci va relancer le build frontend, attendre sa fin, puis redémarrer le backend. Les connexions actives seront interrompues quelques secondes le temps du redémarrage backend."
          confirmLabel="Tout redémarrer"
          danger
          loading={anyBusy}
          onConfirm={handleRestartAll}
          onCancel={() => setConfirmRestartAll(false)}
        />
      )}
    </div>
  );
}
